import { Card, Statistic, Progress, theme } from "antd";
import { ReactElement, ReactNode } from "react";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { formatDuration } from "src/utils/duration";
import { MetricType } from "./DynamicMetrics";
import { CustomTooltip } from "src/components/CustomTooltip";

interface CompoundStatisticProps {
  title: string;
  value: string | number | ReactElement;
  icon?: ReactElement;
  tooltip?: ReactNode;
  trend?: number; // Percentage change
  showProgress?: boolean;
  progressPercent?: number;
  type?: MetricType;
  precision?: number;
  prefix?: ReactElement | string;
  suffix?: ReactElement | string;
  style?: React.CSSProperties;
}

export const CompoundStatistic: React.FC<CompoundStatisticProps> = ({
  title,
  value,
  icon,
  tooltip,
  trend,
  showProgress = false,
  progressPercent,
  type = "number",
  precision,
  prefix,
  suffix,
  style,
}) => {
  const { token } = theme.useToken();

  // Format the value based on type
  const formattedValue = (() => {
    if (typeof value === "object") {
      return value;
    }
    if (type === "duration" && typeof value === "number") {
      return formatDuration(value, { style: "human" });
    }
    if (typeof value === "number") {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      });
    }
    return value;
  })();

  // Determine trend color
  const trendColor = trend
    ? trend > 0
      ? token.colorSuccess
      : token.colorError
    : undefined;

  const cardContent = (
    <Card
      size="small"
      style={{
        height: "100%",
        backgroundColor: token.colorBgContainer,
        ...style,
      }}
      bodyStyle={{
        padding: "16px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: "50px",
        }}
      >
        {/* Header Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          {icon && (
            <span
              style={{
                fontSize: "16px",
                color: token.colorTextSecondary,
                opacity: 0.8,
                display: "flex",
                alignItems: "center",
              }}
            >
              {icon}
            </span>
          )}
          <span
            style={{
              color: token.colorTextSecondary,
              fontSize: "14px",
              fontWeight: 500,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </span>
        </div>

        {/* Value Section */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "8px",
              minHeight: "32px",
            }}
          >
            <span
              style={{
                fontSize: "24px",
                fontWeight: 600,
                color: token.colorText,
                lineHeight: 1,
                display: "flex",
                alignItems: "baseline",
                gap: "4px",
              }}
            >
              {prefix}
              {formattedValue}
              {suffix}
            </span>

            {trend !== undefined && (
              <span
                style={{
                  color: trendColor,
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                  marginLeft: "auto",
                }}
              >
                {trend > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                {Math.abs(trend)}%
              </span>
            )}
          </div>

          {/* Progress Bar */}
          {showProgress && progressPercent !== undefined && (
            <Progress
              percent={progressPercent}
              size="small"
              showInfo={false}
              strokeColor={token.colorPrimary}
              trailColor={token.colorFillQuaternary}
              style={{
                marginBottom: 0,
                padding: "4px 0",
              }}
            />
          )}
        </div>
      </div>
    </Card>
  );

  return tooltip ? (
    <CustomTooltip
      content={tooltip}
      size="medium"
      placement="top"
      mouseEnterDelay={0.1}
    >
      {cardContent}
    </CustomTooltip>
  ) : (
    cardContent
  );
};
